import { customElement, property, query } from "lit/decorators.js";
import { html, LitElement, TemplateResult } from "lit";
import { ifDefined } from "lit/directives/if-defined.js";
import { styleMap } from "lit/directives/style-map.js";
import { IconDefaultType } from "@slhuiicon/icon-core";
import { ClassInfo, classMap } from "lit/directives/class-map.js";
import {
  UserType,
  PixelSizes,
  Sizes,
  AVATAR_BACKGROUND_COLORS,
} from "./avatar.type";
import { style } from "./assets/avatar.generated-style";

/**
 * @customElement slh-nav-avatar
 *
 * @cssprop --slh-color-neutral-shade-00 - Avatar font color.
 * @cssprop --slh-default-font-family - Avatar font family.
 * @cssprop --slh-base-xs-font-size - Avatar font size.
 * @cssprop --slh-base-bold-font-weight - Avatar font weight.
 * @cssprop --slh-color-neutral-shade-40 - Avatar icon color.
 * @cssprop --slh-color-outline - Avatar focus visible outline color.
 */
@customElement("slh-nav-avatar")
export default class SlhNavigationAvatar extends LitElement {
  static styles = style;

  @query(".avatar", false)
  avatar: HTMLElement | undefined;

  /**
   * Image path
   */
  @property({
    type: String,
  })
  src?: string;

  /**
   * User full name
   */
  @property({
    type: Object,
    attribute: true,
  })
  user?: UserType;

  /**
   * Avatar size
   */
  @property({
    type: String,
    attribute: true,
  })
  size: Sizes = Sizes.MD;

  /**
   * @returns render the image
   */
  renderImage(): TemplateResult {
    return html`<div
      class="avatar avatar-image ${classMap(this.renderClasses())}"
      style="${styleMap({ backgroundImage: `url(${this.src})` })}"
    ></div>`;
  }

  /**
   * @returns render icon
   */

  renderIcon(): TemplateResult {
    return html`<slh-icon
      type="${IconDefaultType.ICO_AVATAR}"
      width="${PixelSizes[this.size]}"
      height="${PixelSizes[this.size]}"
    ></slh-icon>`;
  }

  /**
   * @returns render the initials
   */
  renderInitials(): TemplateResult {
    return html`<div class="avatar ${classMap(this.renderClasses())}">
      <span>${this.getInitials()}</span>
    </div>`;
  }

  /**
   *
   * @returns {ClassInfo} ClassList
   */
  renderClasses(): ClassInfo {
    return {
      small: this.size === Sizes.SM,
    };
  }

  /**
   * @returns render the avatar
   */
  renderAvatar(): TemplateResult {
    if (this.src) {
      return this.renderImage();
    }
    if (this.isUser()) {
      return this.renderInitials();
    }
    return this.renderIcon();
  }

  /**
   * @returns fist letters of user full name
   */
  getInitials(): string {
    const fullName = this.user?.fullName.trim();
    const nameApart = fullName?.split(" ");
    let initials = "";
    if (nameApart) {
      if (nameApart?.length === 1) {
        initials = nameApart[0]?.substring(0, 2);
      } else {
        initials =
          nameApart[0].substring(0, 1) +
          nameApart[nameApart.length - 1].substring(0, 1);
      }
    }
    return initials.toUpperCase();
  }

  isUser() {
    return (
      this.user && JSON.stringify(this.user) !== "{}" && this.user.fullName
    );
  }

  /**
   * @returns render html template
   */
  render(): TemplateResult {
    return html`
      <div
        class="slh-nav-avatar"
        aria-label="${ifDefined(
          this.isUser() ? this.user?.fullName : undefined,
        )}"
        role="img"
      >
        ${this.renderAvatar()}
      </div>
    `;
  }

  /**
   * @returns a color value from the defined array by converting each character of the user full name to an ASCII code using charCodeAt,
   * then sums all these numbers and uses that number as the array index
   */
  getFullNameColor(): string {
    const hashString = (string: string) =>
      string
        .split("")
        .map((char) => char.charCodeAt(0))
        .reduce((a, b) => a + b, 0);

    if (this.user) {
      return AVATAR_BACKGROUND_COLORS[
        hashString(this.user.fullName.trim()) % AVATAR_BACKGROUND_COLORS.length
      ];
    }
    return "";
  }

  protected firstUpdated(): void {
    if (this.getAttribute("tabindex") === null) {
      this.tabIndex = 0;
    }
    if (this.isUser()) {
      this.setAttribute("aria-label", this.user ? this.user.fullName : "");
    }
    if (this.src) {
      this.setAttribute("role", "img");
    }
    if (this.avatar) {
      this.avatar.style.backgroundColor = this.getFullNameColor();
    }
  }
}
